import type { DiscountCodePreviewView } from "@keydials/api-client";

/**
 * Discount codes at checkout (KEY-642), the words and the shape. Nothing here decides whether a code is
 * good — the server does that against the plan being bought — this only reads a code off a link, tidies
 * what somebody typed, and says what a code the server accepted takes off.
 */

/** The same shape the server accepts: letters, digits and hyphens, up to 40. */
const CODE_SHAPE = /^[A-Z0-9-]{1,40}$/;

/** A typed code, trimmed and upper-cased, or null when it is not the shape of a code at all. */
export function normalise(raw: string | null | undefined): string | null {
  const code = (raw ?? "").trim().toUpperCase();
  return CODE_SHAPE.test(code) ? code : null;
}

/**
 * The code a link carried — `?code=LAUNCH20`, as a launch email or a friends-and-family message would
 * send it — or "" when there is none, or it is not the shape of one. A mangled code in a link is dropped
 * rather than shown as an error the buyer did not cause.
 */
export function readCode(search: string): string {
  return normalise(new URLSearchParams(search).get("code")) ?? "";
}

/** "20%", "12.5%". */
function percent(value: number): string {
  return `${Number.isInteger(value) ? value : value.toFixed(1).replace(/\.0$/, "")}%`;
}

/** "£10", "£7.50". */
function pounds(pence: number): string {
  return (pence / 100).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: pence % 100 === 0 ? 0 : 2,
  });
}

/**
 * What a code takes off, for the plan's billing period: "20% off your first year" on an annual plan,
 * "20% off your first 12 months" on a monthly one. Given the plan's price, it also says what comes after —
 * "…, then £42 a year" — so nobody reads a first-year discount as the price for good and is surprised at
 * the renewal (asked for on the 25 Sep call). A "forever" discount has no after, so it gets no tail.
 *
 * The period matters because Stripe applies a repeating discount to every invoice raised inside its
 * window. Twelve months on an annual plan is one invoice — the first year — and saying "12 months" there
 * would read as though the second year's renewal were discounted too.
 */
export function discountLabel(preview: DiscountCodePreviewView, period: string, fullPricePence?: number | null): string {
  const lead = leadLabel(preview, period);
  if (fullPricePence == null || preview.duration === "forever") return lead;
  return `${lead}, then ${pounds(fullPricePence)} ${period === "Annual" ? "a year" : "a month"}`;
}

function leadLabel(preview: DiscountCodePreviewView, period: string): string {
  const off =
    preview.percentOff !== null
      ? `${percent(preview.percentOff)} off`
      : preview.amountOffPence !== null
        ? `${pounds(preview.amountOffPence)} off`
        : "A discount";
  const annual = period === "Annual";

  switch (preview.duration) {
    case "forever":
      return `${off} for as long as you subscribe`;
    case "once":
      return `${off} your first payment`;
    case "repeating": {
      const months = preview.durationInMonths ?? 1;
      if (annual) {
        const years = Math.max(1, Math.ceil(months / 12));
        return years === 1 ? `${off} your first year` : `${off} your first ${years} years`;
      }
      return months === 1 ? `${off} your first month` : `${off} your first ${months} months`;
    }
    default:
      return off;
  }
}
